/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import TextField from '@mui/material/TextField';
import Button from '@mui/material/Button';
import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import IconButton from '@mui/material/IconButton';
import Avatar from '@mui/material/Avatar';
import { formatPhoneNumber } from '../utils/phoneFormat';
import Chip from '@mui/material/Chip';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import Container from '@mui/material/Container';

import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';

import DeleteIcon from '@mui/icons-material/Delete';
import SvgIcon from '@mui/material/SvgIcon';
import { ReactComponent as SidebarStoreIcon } from '../assets/icons/sidebar_store_settings.svg';
import { ReactComponent as StoreAvatarPlaceholderIcon } from '../assets/icons/store-avatar-placeholder.svg';
import PageHeader from '../components/PageHeader';
import AddItemButton from '../components/AddItemButton';
import { ReactComponent as ShieldKeyIcon } from '../assets/icons/shield-key.svg';
import { ReactComponent as LiffIdIcon } from '../assets/icons/liff-id.svg';

import { StoreInfo, AdminStoreDetail } from '../types/index';
import api, {
  storeApi,
  extractFieldErrorFromApiError,
  extractValidationFieldErrors,
  getApiErrorMessage,
} from '../services/api';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useManagementAuth } from '../auth/ManagementAuthContext';
import { IMAGE_ACCEPT_JPEG_PNG, IMAGE_TYPE_ERROR_MESSAGE, isJpegOrPng } from '../utils/imageUpload';

// ── スタイル定数（InitialSetupModal と共通） ──────────────────────────

const normalFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    '& fieldset': { border: 'none' },
    '& input': { color: '#212121', fontSize: '15px', padding: '14px 16px' },
    '& input::placeholder': { color: '#BDBDBD', opacity: 1 },
  },
};

const secureFieldSx = {
  '& .MuiOutlinedInput-root': {
    backgroundColor: '#F5F5F5',
    borderRadius: '12px',
    '& fieldset': { border: 'none' },
    '& input': { color: '#212121', fontSize: '15px' },
    '& input::placeholder': { color: '#BDBDBD', opacity: 1 },
  },
};

// ── 422 のフィールドエラー表示用ラベル ────────────────────────────────
// サーバの 422 は detail=[{loc:["body","<field>"], type:...}] で返る。ここに載っている
// 項目だけ入力欄の helperText に落とし、載っていない項目は従来どおり全体エラーにする。
// 住所は参照のみ（入力欄が無く helperText を出す先が無い）ので意図的に載せない。
const STORE_FIELD_LABELS: Record<string, string> = {
  name: '店舗名',
  phone: '電話番号',
  liff_id: 'LIFF ID',
  line_channel_secret: 'チャネルシークレット',
  line_channel_access_token: 'チャネルアクセストークン',
};

// ── 型定義 ────────────────────────────────────────────────────────────

interface EditableStoreInfo {
  name: string;
  address: string;
  phone: string;
  liff_id: string;
  line_channel_secret: string;
  line_channel_access_token: string;
}

type StoreSettingsViewModel = StoreInfo & Partial<AdminStoreDetail>;

// ── バリデーション ─────────────────────────────────────────────────────

const validateLiffId = (v: string) => /^[0-9]{10}-[a-zA-Z0-9]{8}$/.test(v);
const validateChannelSecret = (v: string) => /^[a-zA-Z0-9/]+$/.test(v);
const validateChannelToken = (v: string) => /^[a-zA-Z0-9+/=]+$/.test(v);

// ── 参照モード用フィールド表示コンポーネント ────────────────────────

const ViewField = ({
  label,
  value,
  valueIcon,
  note,
  bold,
}: {
  label: string;
  value: React.ReactNode;
  valueIcon?: React.ReactNode;
  note?: string;
  bold?: boolean;
}) => (
  <>
    <Box sx={{ py: 2 }}>
      <Typography sx={{ fontSize: '12px', color: '#757575', mb: 0.75 }}>
        {label}
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
        {valueIcon}
        {typeof value === 'string'
          ? <Typography sx={{ fontSize: '15px', color: '#3c3c3c', fontWeight: bold ? 'bold' : 'normal' }}>{value || '-'}</Typography>
          : value
        }
      </Box>
      {note && (
        <Typography sx={{ fontSize: '11px', color: '#BDBDBD', mt: 0.5 }}>
          {note}
        </Typography>
      )}
    </Box>
    <Divider sx={{ mx: { xs: -2, sm: 0 } }} />
  </>
);

// ── メインコンポーネント ──────────────────────────────────────────────

const StoreSettings: React.FC = () => {
  const { storeId, refreshStoreName } = useAuth();
  const { isManagementUser, managementAccount } = useManagementAuth();
  const navigate = useNavigate();

  const [storeInfo, setStoreInfo] = useState<StoreSettingsViewModel | null>(null);
  const [editedStoreInfo, setEditedStoreInfo] = useState<EditableStoreInfo>({
    name: '', address: '', phone: '', liff_id: '', line_channel_secret: '', line_channel_access_token: '',
  });

  const [selectedImage, setSelectedImage] = useState<File | null>(null);
  const [serverImageUrl, setServerImageUrl] = useState<string | null>(null);
  const [localImagePreview, setLocalImagePreview] = useState<string | null>(null);
  const [hasImageChanged, setHasImageChanged] = useState(false);
  const [isImageRemoved, setIsImageRemoved] = useState(false);

  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [fieldErrors, setFieldErrors] = useState<Record<string, string>>({});
  const [successMessage, setSuccessMessage] = useState<string | null>(null);
  const [isEditMode, setIsEditMode] = useState(false);
  const [showDeleteImageDialog, setShowDeleteImageDialog] = useState(false);

  const fileInputRef = useRef<HTMLInputElement>(null);

  // ── データ取得 ──────────────────────────────────────────────────────

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        if (!storeId) { setError('店舗IDが取得できませんでした'); return; }
        const [storeDetailData, storeData] = await Promise.all([
          api.adminStore.getStoreDetail(storeId),
          storeApi.getStoreInfo(storeId),
        ]);
        const combined: StoreSettingsViewModel = { ...storeData, ...storeDetailData };
        setStoreInfo(combined);
        setEditedStoreInfo({
          name: combined.name || '',
          address: combined.address || '',
          phone: combined.phone || '',
          liff_id: storeDetailData.liff_id || '',
          line_channel_secret: '',
          line_channel_access_token: '',
        });
        if (storeData.image_url) setServerImageUrl(storeData.image_url);
        setError(null);
      } catch (err: any) {
        setError(err.message?.includes('500')
          ? 'サーバーエラーが発生しました。'
          : 'データの取得に失敗しました。ネットワーク接続を確認してください。'
        );
      } finally {
        setIsLoading(false);
      }
    };
    fetchData();
  }, [storeId]);

  // ── ハンドラ ────────────────────────────────────────────────────────

  const handleStoreInfoChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, value } = e.target;
    let processed = value;
    if (name === 'phone') {
      processed = formatPhoneNumber(value);
    } else if (['liff_id', 'line_channel_secret', 'line_channel_access_token'].includes(name)) {
      processed = value.replace(/\s/g, '');
    }
    setEditedStoreInfo(prev => ({ ...prev, [name]: processed }));
    if (fieldErrors[name]) {
      setFieldErrors(prev => { const next = { ...prev }; delete next[name]; return next; });
    }
  };

  const handleImageSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (file.size > 5 * 1024 * 1024) { setError('画像ファイルのサイズは5MB以下にしてください。'); return; }
    if (!isJpegOrPng(file)) { setError(IMAGE_TYPE_ERROR_MESSAGE); return; }
    setSelectedImage(file);
    setHasImageChanged(true);
    setIsImageRemoved(false);
    const reader = new FileReader();
    reader.onload = (ev) => setLocalImagePreview(ev.target?.result as string);
    reader.readAsDataURL(file);
    setError(null);
  };

  const handleDeleteImage = () => {
    setLocalImagePreview(null);
    setSelectedImage(null);
    setHasImageChanged(true);
    setIsImageRemoved(true);
    setShowDeleteImageDialog(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  };

  const handleEnterEditMode = () => {
    setIsEditMode(true);
    setError(null);
    setFieldErrors({});
  };

  const handleCancelEdit = () => {
    if (storeInfo) {
      setEditedStoreInfo({
        name: storeInfo.name || '',
        address: storeInfo.address || '',
        phone: storeInfo.phone || '',
        liff_id: storeInfo.liff_id || '',
        line_channel_secret: '',
        line_channel_access_token: '',
      });
    }
    setLocalImagePreview(null);
    setSelectedImage(null);
    setHasImageChanged(false);
    setIsImageRemoved(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
    setIsEditMode(false);
    setError(null);
    setFieldErrors({});
  };

  const scrollToField = (fieldName: string) => {
    setTimeout(() => {
      const el = document.querySelector(`[name="${fieldName}"]`);
      if (el) {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
        (el as HTMLElement).focus();
      }
    }, 100);
  };

  const handleSaveStoreInfo = async () => {
    if (!storeInfo?.id) return;

    const errors: Record<string, string> = {};
    if (!editedStoreInfo.name.trim()) {
      errors.name = '店舗名は必須です';
    }
    if (editedStoreInfo.liff_id && !validateLiffId(editedStoreInfo.liff_id)) {
      errors.liff_id = 'LIFF IDは数値10桁+ハイフン+英数8桁の形式で入力してください（例：1234567890-abcdefgh）';
    }
    if (editedStoreInfo.line_channel_secret && !validateChannelSecret(editedStoreInfo.line_channel_secret)) {
      errors.line_channel_secret = 'LINEチャネルシークレットは半角英数字とスラッシュのみ使用可能です';
    }
    if (editedStoreInfo.line_channel_access_token && !validateChannelToken(editedStoreInfo.line_channel_access_token)) {
      errors.line_channel_access_token = 'LINEチャネルアクセストークンは半角英数字と記号(+/=)のみ使用可能です';
    }

    if (Object.keys(errors).length > 0) {
      setFieldErrors(errors);
      const firstErrorField = Object.keys(errors)[0];
      scrollToField(firstErrorField);
      return;
    }

    setIsSaving(true);
    setError(null);
    setFieldErrors({});
    try {
      // 保存前にLINE認証情報の重複チェック（自店舗は除外）
      const tokenForCheck = editedStoreInfo.line_channel_access_token.trim();
      const secretForCheck = editedStoreInfo.line_channel_secret.trim();
      if (tokenForCheck || secretForCheck) {
        const result = await api.adminStore.checkLineCredentials({
          line_channel_access_token: tokenForCheck || undefined,
          line_channel_secret: secretForCheck || undefined,
          exclude_store_id: storeInfo.id,
        });
        if (!result.is_available && result.conflicting_field) {
          setFieldErrors({
            [result.conflicting_field]: result.message || '入力された値は既に別の店舗に登録されています',
          });
          scrollToField(result.conflicting_field);
          return;
        }
      }

      const updateData: any = {
        name: editedStoreInfo.name,
        // 電話番号は任意。空でもそのまま送る（サーバ側で空文字を許容）
        phone: editedStoreInfo.phone,
      };
      // LIFF ID は未設定のまま保存できるようにするため、空のときは項目ごと送らない
      // （空文字を送るとサーバのバリデーションで 422 になる）
      if (editedStoreInfo.liff_id.trim()) updateData.liff_id = editedStoreInfo.liff_id;
      if (editedStoreInfo.line_channel_secret.trim()) updateData.line_channel_secret = editedStoreInfo.line_channel_secret;
      if (editedStoreInfo.line_channel_access_token.trim()) updateData.line_channel_access_token = editedStoreInfo.line_channel_access_token;

      const updatedStore = await api.adminStore.updateStore(storeInfo.id, updateData);

      let newImageUrl = serverImageUrl;
      if (hasImageChanged) {
        if (isImageRemoved && serverImageUrl) {
          await storeApi.deleteStoreImage(storeInfo.id);
          newImageUrl = null;
        } else if (selectedImage) {
          const res = await storeApi.uploadStoreImage(storeInfo.id, selectedImage);
          if (res.image_url) newImageUrl = res.image_url;
        }
      }

      const latestStoreDetail = await api.adminStore.getStoreDetail(storeInfo.id);
      const mergedStoreInfo: StoreSettingsViewModel = {
        ...storeInfo,
        ...updatedStore,
        ...latestStoreDetail,
        image_url: newImageUrl ?? storeInfo?.image_url ?? '',
      };

      setStoreInfo(mergedStoreInfo);
      setEditedStoreInfo({
        name: mergedStoreInfo.name || editedStoreInfo.name,
        address: mergedStoreInfo.address || editedStoreInfo.address,
        phone: mergedStoreInfo.phone || editedStoreInfo.phone,
        liff_id: mergedStoreInfo.liff_id || editedStoreInfo.liff_id,
        line_channel_secret: '',
        line_channel_access_token: '',
      });
      setServerImageUrl(newImageUrl);
      setLocalImagePreview(null);
      setSelectedImage(null);
      setHasImageChanged(false);
      setIsImageRemoved(false);
      setIsEditMode(false);
      setSuccessMessage('店舗情報を更新しました');
      setTimeout(() => setSuccessMessage(null), 3000);
      await refreshStoreName();
    } catch (err: any) {
      // バックエンドから特定フィールドに紐付くエラー（LINE認証情報の重複など）が返ってきた場合は
      // 該当フィールドの helperText に表示し、入力枠を赤くする
      const fieldError = extractFieldErrorFromApiError(err);
      if (fieldError) {
        setFieldErrors(prev => ({ ...prev, [fieldError.field]: fieldError.message }));
        scrollToField(fieldError.field);
      } else {
        // 422（リクエスト検証エラー）は「リクエストの形式が不正です」だけでは
        // どの項目が悪いか分からないため、入力欄ごとの helperText に落とす（設計書 §3.4(b)）
        const validationErrors = extractValidationFieldErrors(err, STORE_FIELD_LABELS);
        const firstField = Object.keys(validationErrors)[0];
        if (firstField) {
          setFieldErrors(prev => ({ ...prev, ...validationErrors }));
          scrollToField(firstField);
        } else {
          setError(getApiErrorMessage(err, '店舗情報の更新に失敗しました。もう一度お試しください。'));
        }
      }
    } finally {
      setIsSaving(false);
    }
  };

  const displayImage = localImagePreview || (isImageRemoved ? null : serverImageUrl);


  // ── レンダリング ─────────────────────────────────────────────────────

  return (
    <Box sx={{ flexGrow: 1 }}>

      <PageHeader
        title="店舗設定"
        icon={<SvgIcon component={SidebarStoreIcon} inheritViewBox sx={{ fontSize: 22, color: '#898989' }} />}
        action={!isLoading && !isEditMode && (
          <Button onClick={handleEnterEditMode} sx={{ mr: { xs: 1, sm: 0 } }}>
            編集
          </Button>
        )}
      />

      {/* アラート（API エラーなど非フィールドエラー用） */}
      {successMessage && (
        <Alert severity="success" onClose={() => setSuccessMessage(null)} sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}
      {error && (
        <Alert severity="error" onClose={() => setError(null)} sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      {isLoading ? (
        <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      ) : (
        <Container maxWidth="sm" disableGutters>

          {/* 店舗画像 */}
          <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', py: 3 }}>
            <Box sx={{ position: 'relative' }}>
              <Avatar
                src={displayImage || undefined}
                sx={{
                  width: 110, height: 110, bgcolor: '#f3f3f3',
                  border: '1px solid #e7e7e7',
                  cursor: isEditMode ? 'pointer' : 'default',
                }}
                onClick={isEditMode ? () => fileInputRef.current?.click() : undefined}
              >
                <SvgIcon component={StoreAvatarPlaceholderIcon} inheritViewBox sx={{ fontSize: 20, color: '#898989' }} />
              </Avatar>
              {isEditMode && displayImage && (
                <IconButton
                  size="small"
                  onClick={() => setShowDeleteImageDialog(true)}
                  sx={{
                    position: 'absolute', top: -4, right: -4,
                    bgcolor: 'white', width: 24, height: 24,
                    border: '1px solid #e0e0e0',
                    '&:hover': { bgcolor: '#fafafa' },
                  }}
                >
                  <DeleteIcon sx={{ fontSize: 14, color: '#f44336' }} />
                </IconButton>
              )}
            </Box>

            {isEditMode && (
              <>
                <AddItemButton
                  onClick={() => fileInputRef.current?.click()}
                  label="画像を挿入する"
                  showIcon={false}
                  sx={{ mt: -1.5, zIndex: 1 }}
                />
                <Typography sx={{ fontSize: '11px', color: '#BDBDBD', mt: 0.5 }}>
                  JPG・PNG形式、5MB以下
                </Typography>
              </>
            )}
            <input
              ref={fileInputRef}
              type="file"
              accept={IMAGE_ACCEPT_JPEG_PNG}
              onChange={handleImageSelect}
              style={{ display: 'none' }}
            />
          </Box>

          {/* 店舗ID */}
          <Typography
            fontWeight="bold"
            sx={{ textAlign: 'center', color: '#3c3c3c', mb: 3, fontSize: '14px' }}
          >
            店舗ID / {storeId || '-'}
          </Typography>

          <Divider sx={{ mx: { xs: -2, sm: 0 } }} />

          {/* 店舗名 */}
          {!isEditMode ? (
            <ViewField
              label="店舗名"
              value={editedStoreInfo.name}
              bold
            />
          ) : (
            <>
              <Box sx={{ py: 2 }}>
                <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>店舗名</Typography>
                <TextField
                  name="name"
                  value={editedStoreInfo.name}
                  onChange={handleStoreInfoChange}
                  fullWidth
                  placeholder="店舗名を入力"
                  error={!!fieldErrors.name}
                  helperText={fieldErrors.name}
                  sx={normalFieldSx}
                />
              </Box>
              <Divider sx={{ mx: { xs: -2, sm: 0 } }} />
            </>
          )}

          {/* 住所（常に参照のみ） */}
          <ViewField
            label="住所"
            value={editedStoreInfo.address}
            bold
          />

          {/* 電話番号 */}
          {!isEditMode ? (
            <ViewField
              label="電話番号"
              value={editedStoreInfo.phone}
              bold
            />
          ) : (
            <>
              <Box sx={{ py: 2 }}>
                <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>電話番号</Typography>
                <TextField
                  name="phone"
                  value={editedStoreInfo.phone}
                  onChange={handleStoreInfoChange}
                  fullWidth
                  type="tel"
                  placeholder="例: 03-1234-5678"
                  error={!!fieldErrors.phone}
                  helperText={fieldErrors.phone}
                  sx={normalFieldSx}
                />
              </Box>
              <Divider sx={{ mx: { xs: -2, sm: 0 } }} />
            </>
          )}

          {/* LIFF ID */}
          {!isEditMode ? (
            <ViewField
              label="LIFF ID"
              valueIcon={<SvgIcon component={LiffIdIcon} inheritViewBox sx={{ fontSize: 22, color: '#898989' }} />}
              // LIFF ID は秘匿情報ではないため値をそのまま表示する（シークレット類のような
              // 「設定済み」チップにはしない＝値が見えるほうが誤入力に気づける）。
              // 未設定のときだけ明示する。未設定だと出退勤QRを発行できないため。
              value={
                editedStoreInfo.liff_id || (
                  <Chip label="未設定" size="small" color="warning" sx={{ fontSize: '12px' }} />
                )
              }
            />
          ) : (
            <>
              <Box sx={{ py: 2 }}>
                <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>LIFF ID</Typography>
                <TextField
                  name="liff_id"
                  value={editedStoreInfo.liff_id}
                  onChange={handleStoreInfoChange}
                  fullWidth
                  placeholder="LIFF ID"
                  error={!!fieldErrors.liff_id}
                  helperText={fieldErrors.liff_id || '数値10桁+ハイフン+英数8桁'}
                  sx={secureFieldSx}
                  FormHelperTextProps={{
                    sx: fieldErrors.liff_id
                      ? { color: '#d32f2f', fontSize: '12px', mt: 0.5 }
                      : { color: '#BDBDBD', fontSize: '11px', mt: 0.5 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon component={LiffIdIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Divider sx={{ mx: { xs: -2, sm: 0 } }} />
            </>
          )}

          {/* LINEチャネルシークレット */}
          {!isEditMode ? (
            <ViewField
              label="LINEチャネルシークレット"
              valueIcon={<SvgIcon component={ShieldKeyIcon} inheritViewBox sx={{ fontSize: 22, color: '#898989' }} />}
              value={
                <Chip
                  label={storeInfo?.has_line_channel_secret ? '設定済み' : '未設定'}
                  size="small"
                  color={storeInfo?.has_line_channel_secret ? 'success' : 'default'}
                  sx={{ fontSize: '12px' }}
                />
              }
            />
          ) : (
            <>
              <Box sx={{ py: 2 }}>
                <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>LINEチャネルシークレット</Typography>
                <TextField
                  name="line_channel_secret"
                  value={editedStoreInfo.line_channel_secret}
                  onChange={handleStoreInfoChange}
                  fullWidth
                  placeholder="LINEチャネルシークレット"
                  type="password"
                  autoComplete="new-password"
                  error={!!fieldErrors.line_channel_secret}
                  helperText={fieldErrors.line_channel_secret || '半角英数字とスラッシュのみ使用可能'}
                  sx={secureFieldSx}
                  FormHelperTextProps={{
                    sx: fieldErrors.line_channel_secret
                      ? { color: '#d32f2f', fontSize: '12px', mt: 0.5 }
                      : { color: '#BDBDBD', fontSize: '11px', mt: 0.5 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon component={ShieldKeyIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Divider sx={{ mx: { xs: -2, sm: 0 } }} />
            </>
          )}

          {/* LINEチャネルアクセストークン */}
          {!isEditMode ? (
            <ViewField
              label="LINEチャネルアクセストークン"
              valueIcon={<SvgIcon component={ShieldKeyIcon} inheritViewBox sx={{ fontSize: 22, color: '#898989' }} />}
              value={
                <Chip
                  label={storeInfo?.has_line_channel_access_token ? '設定済み' : '未設定'}
                  size="small"
                  color={storeInfo?.has_line_channel_access_token ? 'success' : 'default'}
                  sx={{ fontSize: '12px' }}
                />
              }
            />
          ) : (
            <>
              <Box sx={{ py: 2 }}>
                <Typography sx={{ fontSize: '13px', color: '#3c3c3c', mb: 0.75 }}>LINEチャネルアクセストークン</Typography>
                <TextField
                  name="line_channel_access_token"
                  value={editedStoreInfo.line_channel_access_token}
                  onChange={handleStoreInfoChange}
                  fullWidth
                  placeholder="LINEチャネルアクセストークン"
                  type="password"
                  autoComplete="new-password"
                  error={!!fieldErrors.line_channel_access_token}
                  helperText={fieldErrors.line_channel_access_token || '半角英数字と記号(+/=)が使用可能'}
                  sx={secureFieldSx}
                  FormHelperTextProps={{
                    sx: fieldErrors.line_channel_access_token
                      ? { color: '#d32f2f', fontSize: '12px', mt: 0.5 }
                      : { color: '#BDBDBD', fontSize: '11px', mt: 0.5 },
                  }}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SvgIcon component={ShieldKeyIcon} inheritViewBox sx={{ color: '#898989', fontSize: 22 }} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              <Divider sx={{ mx: { xs: -2, sm: 0 } }} />
            </>
          )}

          {/* フッター（編集モード時） */}
          {isEditMode && (
            <Box sx={{ pt: 3, pb: 2, display: 'flex', gap: 1.5 }}>
              <Button
                onClick={handleCancelEdit}
                disabled={isSaving}
                sx={{
                  flex: 1,
                  bgcolor: '#E0E0E0', color: '#616161',
                  borderRadius: '12px', height: 52,
                  fontSize: '15px', fontWeight: 'bold',
                  textTransform: 'none', boxShadow: 'none',
                  '&:hover': { bgcolor: '#BDBDBD', boxShadow: 'none' },
                }}
              >
                キャンセル
              </Button>
              <Button
                variant="contained"
                onClick={handleSaveStoreInfo}
                disabled={isSaving}
                startIcon={isSaving ? <CircularProgress size={20} color="inherit" /> : null}
                sx={{
                  flex: 3,
                  bgcolor: '#1565C0',
                  borderRadius: '12px', height: 52,
                  fontSize: '16px', fontWeight: 'bold',
                  textTransform: 'none',
                  '&:hover': { bgcolor: '#0D47A1' },
                  '&.Mui-disabled': { bgcolor: '#90A4AE', color: 'white' },
                }}
              >
                {isSaving ? '保存中...' : '保存'}
              </Button>
            </Box>
          )}

          {/* 企業登録セクション */}
          {!isEditMode && (
            <Box sx={{ mt: 4, mb: 2 }}>
              <Divider sx={{ mx: { xs: -2, sm: 0 }, mb: 3 }} />
              {isManagementUser ? (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => {
                    const companyId = managementAccount?.companies?.[0]?.id;
                    navigate(companyId ? `/management/company/${companyId}` : '/management');
                  }}
                  sx={{
                    borderRadius: '12px', height: 52,
                    fontSize: '15px', fontWeight: 'bold',
                    textTransform: 'none',
                    borderColor: '#1565C0', color: '#1565C0',
                  }}
                >
                  企業管理画面へ
                </Button>
              ) : (
                <Button
                  fullWidth
                  variant="outlined"
                  onClick={() => navigate('/company-registration')}
                  sx={{
                    borderRadius: '12px', height: 52,
                    fontSize: '15px', fontWeight: 'bold',
                    textTransform: 'none',
                    borderColor: '#1565C0', color: '#1565C0',
                  }}
                >
                  企業登録を申請する
                </Button>
              )}
            </Box>
          )}

        </Container>
      )}

      {/* 画像削除確認ダイアログ */}
      <Dialog open={showDeleteImageDialog} onClose={() => setShowDeleteImageDialog(false)}>
        <DialogTitle>画像を削除</DialogTitle>
        <DialogContent>
          <DialogContentText>
            店舗画像を削除してもよろしいですか？この操作は元に戻せません。
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setShowDeleteImageDialog(false)} color="inherit">キャンセル</Button>
          <Button onClick={handleDeleteImage} color="error" variant="contained">削除</Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default StoreSettings;
